export interface CoaFileEntry {
  id: number;
  fileId: number;
  packageId?: number | string | null;
  packageLabel?: string | null;
  facilityId?: number | string | null;
  label?: string | null;
  organizationId?: number | string | null;
  testPerformedDate: string | null;
  displayOrder?: number | null;
  isHidden: boolean;
  fileName: string | null;
  blobName?: string | null;
  isStaged: boolean;
}

export interface PrintRunEntry {
  id: number;
  organizationId?: number | string | null;
  organizationName?: string | null;
  packageId?: number | string | null;
  packageLabel?: string | null;
  facilityId?: number | string | null;
  facilityLicense?: string | null;
  range?: number[][] | null;
  count?: number | null;
  jobName?: string | null;
  jobId?: string | null;
}

export interface CoaFileGroup {
  key: string;
  packageId: number | string | null;
  facilityId: number | string | null;
  organizationId: number | string | null;
  packageLabel: string;
  files: CoaFileEntry[];
  printRuns: PrintRunEntry[];
}

export interface CoaFileMutationResult {
  id: number;
  isStaged: boolean;
  succeeded: boolean;
  reason?: string | null;
}

export interface CoaFileMutationRequestEntry {
  id: number;
  isStaged: boolean;
}

export interface CoaDropLanding {
  groupKey: string;
  targetKey: string;
  edge: 'before' | 'after';
}

export function compositeKey(file: CoaFileEntry): string {
  return `${file.isStaged ? 's' : 'a'}-${file.id}`;
}

export function mutationKey(id: number, isStaged: boolean): string {
  return `${isStaged ? 's' : 'a'}-${id}`;
}

export function packageLabelFor(file: CoaFileEntry, fallbackLabel: string): string {
  return file.packageLabel || file.label || fallbackLabel;
}

export function packageGroupKey(file: CoaFileEntry, fallbackLabel: string): string {
  return `${file.packageId ?? 'unknown'}-${file.facilityId ?? 'unknown'}-${packageLabelFor(file, fallbackLabel)}`;
}

function printRunGroupKey(printRun: PrintRunEntry, fallbackLabel: string): string {
  return `${printRun.packageId ?? 'unknown'}-${printRun.facilityId ?? 'unknown'}-${printRun.packageLabel || fallbackLabel}`;
}

function printRunMatchesPackageGroup(
  printRun: PrintRunEntry,
  packageId: number | string | null,
  packageLabel: string,
  facilityId: number | string | null,
) {
  if (packageId != null && printRun.packageId != null) {
    return String(printRun.packageId) === String(packageId);
  }

  if (facilityId != null && printRun.facilityId != null && String(printRun.facilityId) !== String(facilityId)) {
    return false;
  }

  return (printRun.packageLabel || '').toLowerCase() === packageLabel.toLowerCase();
}

export function formatPrintRunRange(range: number[][] | null | undefined): string {
  if (!Array.isArray(range) || range.length === 0) return '';

  return range
    .map((item) => {
      if (!Array.isArray(item) || item.length < 2) return '';
      const [start, end] = item;
      return start === end ? String(start) : `${start}-${end}`;
    })
    .filter(Boolean)
    .join(', ');
}

function compareCoaFiles(left: CoaFileEntry, right: CoaFileEntry): number {
  const leftOrder = typeof left.displayOrder === 'number' ? left.displayOrder : Number.MAX_SAFE_INTEGER;
  const rightOrder = typeof right.displayOrder === 'number' ? right.displayOrder : Number.MAX_SAFE_INTEGER;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;

  const leftTime = left.testPerformedDate ? new Date(left.testPerformedDate).getTime() : Number.NEGATIVE_INFINITY;
  const rightTime = right.testPerformedDate ? new Date(right.testPerformedDate).getTime() : Number.NEGATIVE_INFINITY;
  const leftSortTime = Number.isFinite(leftTime) ? leftTime : Number.NEGATIVE_INFINITY;
  const rightSortTime = Number.isFinite(rightTime) ? rightTime : Number.NEGATIVE_INFINITY;
  if (leftSortTime !== rightSortTime) return rightSortTime - leftSortTime;

  return right.fileId - left.fileId;
}

export function groupCoaFiles(
  files: CoaFileEntry[],
  printRuns: PrintRunEntry[],
  fallbackLabel: string,
): CoaFileGroup[] {
  const groups = new Map<string, CoaFileGroup>();

  files.forEach((file) => {
    const key = packageGroupKey(file, fallbackLabel);
    const existing = groups.get(key);

    if (existing) {
      existing.files.push(file);
      return;
    }

    groups.set(key, {
      key,
      packageId: file.packageId ?? null,
      facilityId: file.facilityId ?? null,
      organizationId: file.organizationId ?? null,
      packageLabel: packageLabelFor(file, fallbackLabel),
      files: [file],
      printRuns: [],
    });
  });

  const coaBackedGroups = Array.from(groups.values());

  printRuns.forEach((printRun) => {
    const matchingGroup = coaBackedGroups.find((group) =>
      printRunMatchesPackageGroup(printRun, group.packageId, group.packageLabel, group.facilityId),
    );

    if (matchingGroup) {
      matchingGroup.printRuns.push(printRun);
      return;
    }

    const key = printRunGroupKey(printRun, fallbackLabel);
    const existing = groups.get(key);

    if (existing) {
      existing.printRuns.push(printRun);
      return;
    }

    const newGroup: CoaFileGroup = {
      key,
      packageId: printRun.packageId ?? null,
      facilityId: printRun.facilityId ?? null,
      organizationId: printRun.organizationId ?? null,
      packageLabel: printRun.packageLabel || fallbackLabel,
      files: [],
      printRuns: [printRun],
    };

    groups.set(key, newGroup);
    coaBackedGroups.push(newGroup);
  });

  groups.forEach((group) => {
    group.files.sort(compareCoaFiles);
  });

  return Array.from(groups.values());
}

export function buildVisibilityRequests(
  files: CoaFileEntry[],
  selected: Set<string>,
  fallbackLabel: string,
  currentHidden: boolean,
  nextHidden: boolean,
) {
  const requests = new Map<
    string,
    { packageLabel: string; fileEntries: { id: number; isHidden: boolean; isStaged: boolean }[] }
  >();

  files.forEach((file) => {
    if (!selected.has(compositeKey(file)) || file.isHidden !== currentHidden) return;

    const packageLabel = packageLabelFor(file, fallbackLabel);
    const key = packageGroupKey(file, fallbackLabel);
    const entry = requests.get(key) || { packageLabel, fileEntries: [] };
    entry.fileEntries.push({ id: file.id, isHidden: nextHidden, isStaged: file.isStaged });
    requests.set(key, entry);
  });

  return Array.from(requests.values());
}

export function normalizeErrorBody(body: unknown): string {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const maybeError = (body as { error?: unknown }).error;
    if (typeof maybeError === 'string') return maybeError;
    const maybeMessage = (body as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  if (body != null) return JSON.stringify(body);
  return '';
}

export function extractMutationResults(
  result: unknown,
  fallbackEntries: CoaFileMutationRequestEntry[],
): CoaFileMutationResult[] {
  const rawResults = result && typeof result === 'object' && Array.isArray((result as { results?: unknown }).results)
    ? (result as { results: unknown[] }).results
    : null;

  if (!rawResults) {
    return fallbackEntries.map((entry) => ({ ...entry, succeeded: true }));
  }

  return rawResults.map((raw) => {
    const entry = raw as { id?: unknown; isStaged?: unknown; succeeded?: unknown; reason?: unknown };
    return {
      id: Number(entry.id),
      isStaged: Boolean(entry.isStaged),
      succeeded: Boolean(entry.succeeded),
      reason: typeof entry.reason === 'string' ? entry.reason : null,
    };
  }).filter((entry) => Number.isFinite(entry.id) && entry.id > 0);
}

function coaCountLabel(count: number): string {
  return `${count} COA${count === 1 ? '' : 's'}`;
}

export function mutationSummary(
  successCount: number,
  failedResults: CoaFileMutationResult[],
  successAction: string,
  filesByKey: Map<string, CoaFileEntry>,
): string {
  if (failedResults.length === 0) return `${coaCountLabel(successCount)} ${successAction}.`;

  const summary = `${coaCountLabel(successCount)} ${successAction}; ${failedResults.length} failed.`;
  const details = failedResults.slice(0, 3).map((result) => {
    const file = filesByKey.get(mutationKey(result.id, result.isStaged));
    return `${file?.fileName || `file-${result.id}`}: ${result.reason || 'Unknown error'}`;
  });
  const remainingCount = failedResults.length - details.length;

  return `${summary} ${details.join('; ')}${remainingCount > 0 ? `; ${remainingCount} more failed` : ''}.`;
}

export function pendingCoaFileKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}
